<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />
    <div
      class="am3-input-wrapper flex flex-row items-start justify-between rounded-md"
      :class="[
        disabled ? 'disabled' : 'focus-within:outline-brand',
        (disabled) ? disabledClasses : 'bg-surface-light hover:border-brand ',
        (validationState === false || typeof validationState === 'string') ? 'error border border-solid border-[var(--font-colour-warning)]' : buttonBorderClasses,
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-wrapper')"
    >
      <!-- Textarea element -->
      <textarea
        :id="inputId"
        ref="textareaRef"
        class="am3-input-textarea focus:outline-brand focus:outline-offset-0 am3-input bg-transparent px-3 py-2.5 text-sm min-h-20 w-full border-none outline-none focus:ring-0 rounded-md relative"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="rows"
        :maxlength="maxlength"
        :data-test-id="generateDataTestId(dataTestId, 'textarea')"
        @input="updateValue"
        @change="$emit('change', $event.target.value.trim())"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        @blur="handleBlur"
      />
    </div>

    <AM3InputError
      :error="validationState"
      :data-test-id="generateDataTestId(dataTestId, 'input-error')"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

/**
 * AM3InputTextarea component
 *
 * A flexible textarea input component with validation, restrictions, and resizable functionality
 */
export default defineComponent({
  name: 'AM3InputTextarea',
  components: { AM3InputError, AM3InputLabel },

  props: {
    // v-model value
    modelValue: {
      type: [String, Number],
      default: '',
    },

    // Input label (string or false to hide)
    label: {
      type: [String, Boolean] as PropType<string | false>,
      default: '',
    },

    // Placeholder text
    placeholder: {
      type: String,
      default: props => 'Enter ' + props.label.toLowerCase(),
    },

    // Number of visible text lines
    rows: {
      type: Number,
      default: 4,
    },

    // Maximum input length (HTML maxlength attribute)
    maxlength: {
      type: Number,
      default: undefined,
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required field
    required: {
      type: Boolean,
      default: false,
    },

    // Validation function
    validate: {
      type: Function as PropType<(value: string) => boolean | string>,
      default: () => true,
    },

    // Validate on blur
    validateOnBlur: {
      type: Boolean,
      default: false,
    },

    // Input restriction function
    restrict: {
      type: Function as PropType<(event: Event) => boolean>,
      default: () => true,
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.label || 'textarea-input',
    },
  },

  emits: ['update:modelValue', 'change', 'enter', 'blur'],

  setup(props, { emit }) {
    const buttonBorderClasses = ' rounded-md border border-1 border-surface-dark outline-1 outline-offset-0 outline-transparent outline-none'
    const disabledClasses = ' text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none text-white'

    // Generate unique ID for the textarea
    const inputId = ref(`am3-textarea-${uuidv4()}`)
    const textareaRef = ref<HTMLTextAreaElement | null>(null)

    // Setup form validation
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
      validateOnBlur,
      handleBlur: _handleBlur,
    } = useFormValidation(props.validate, props.modelValue, props.required)

    // Sync the validateOnBlur prop with the composable ref
    validateOnBlur.value = props.validateOnBlur

    // Handle blur with custom logic
    const handleBlur = () => {
      _handleBlur(props.modelValue)
      emit('blur')
    }

    // Public method to run validation
    const runValidation = (value = props.modelValue, checkRequired = props.required) => {
      return _runValidation(value, checkRequired)
    }

    // Update model value
    const updateValue = (event: Event) => {
      const target = event.target as HTMLTextAreaElement
      emit('update:modelValue', target.value)
    }

    // Handle keydown event with restrictions
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!props.restrict(event)) {
        event.preventDefault()
      }

      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        emit('enter', (event.target as HTMLTextAreaElement).value)
      }
    }

    // Handle paste event with restrictions
    const handlePaste = (event: ClipboardEvent) => {
      if (!props.restrict(event)) {
        event.preventDefault()
      }
    }

    return {
      inputId,
      textareaRef,
      validationState,
      updateValue,
      handleKeyDown,
      handlePaste,
      buttonBorderClasses,
      disabledClasses,
      generateDataTestId,
      handleBlur,
      runValidation,
      resetValidation,
    }
  },
})
</script>
